import { createModule } from 'saga-slice';
import { ApprovalState, initialState } from './stateType';
import {
  ApprovalProject,
  FinancialSummaryInfo,
  ProjectsApprovalResponse,
} from './types';
import {
  captureRevisedBudgetSagaFactory,
  fetchApprovalProjectSagaFactory,
  fetchApprovalProjectsSagaFactory,
  updateApprovalProjectsSagaFactory,
} from './sagas';
import { updateProjectApprovalFields } from './utils';

const ApprovalSlice = createModule({
  name: 'approval',
  initialState,
  reducers: {
    fetchProjects: (state: ApprovalState) => {
      state.loading = true;
    },
    updateProjects: (state: ApprovalState) => {
      state.loading = true;
    },
    fetchProjectsSuccess: (
      state: ApprovalState,
      payload: ApprovalProject[],
    ) => {
      state.projects = payload;
      state.loading = false;
    },
    fetchProjectsError: (state: ApprovalState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    updateProjectsSuccess: (
      state: ApprovalState,
      payload: ProjectsApprovalResponse,
    ) => {
      state.projects = updateProjectApprovalFields(state.projects, payload);
      state.loading = false;
    },
    updateProjectsError: (state: ApprovalState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    setSelectedProjectId: (state: ApprovalState, payload: string) => {
      state.selectedProjectId = payload;
    },
    fetchApprovalProject: (state: ApprovalState) => {
      state.loading = true;
    },
    fetchApprovalProjectSuccess: (
      state: ApprovalState,
      payload: ApprovalProject,
    ) => {
      state.loading = false;
      const projectIndex = state.projects.findIndex((p) => p.id === payload.id);
      if (projectIndex) {
        const existing = state.projects[projectIndex];
        state.projects[projectIndex] = {
          ...existing,
          ...payload,
          // This endpoint can leave out/nullify this field. Don't let it remove a known value.
          isDirectiveComplete:
            payload.isDirectiveComplete ?? existing.isDirectiveComplete,
        };
      }
    },
    fetchApprovalProjectError: (state: ApprovalState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    captureRevisedBudget: (state: ApprovalState) => {
      state.loading = true;
    },
    captureRevisedBudgetSuccess: (
      state: ApprovalState,
      payload: { [id: string]: FinancialSummaryInfo[] },
    ) => {
      state.loading = false;
      Object.keys(payload).forEach((key: string) => {
        const updates = payload[key].filter(
          (p) => p.financialCategory === 'BASELINE',
        );
        const project = state.projects.findIndex((p) => p.id === key);
        if (project !== -1) {
          const infos = [...state.projects[project].financialSummaryInfos];
          updates.forEach((update) => {
            const existingInfo = infos.findIndex(
              (info) =>
                info.fiscalYear === update.fiscalYear &&
                info.financialCategory === update.financialCategory,
            );
            if (existingInfo !== -1) {
              infos[existingInfo] = { ...update };
            } else {
              infos.push(update);
            }
          });
          state.projects[project].financialSummaryInfos = infos;
        }
      });
    },
    captureRevisedBudgetError: (state: ApprovalState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
  },
  sagas: (actions) => ({
    [actions.fetchProjects]: fetchApprovalProjectsSagaFactory(actions),
    [actions.updateProjects]: updateApprovalProjectsSagaFactory(actions),
    [actions.fetchApprovalProject]: fetchApprovalProjectSagaFactory(actions),
    [actions.captureRevisedBudget]: captureRevisedBudgetSagaFactory(actions),
  }),
});
export default ApprovalSlice;
export const approvalActions = ApprovalSlice.actions;
